import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, UploadCloud } from "lucide-react";
import Switch from "../../../components/settings/Switch";
import { useToast } from "../../../hooks/useToast";
import { useUpdateSettingsMutation } from "../../../api/query/useAdminQueries";
import adminService from "../../../api/services/adminService";
import { useQueryClient } from "@tanstack/react-query";

export default function GeneralPanel({ settings }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [expandedSection, setExpandedSection] = useState("platform");
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const updateSettingsMutation = useUpdateSettingsMutation();

  const [platformConfig, setPlatformConfig] = useState({
    appName: "Pulo",
    primaryCurrency: "NGN - Naira",
    systemTimezone: "(GMT +1) West Africa",
    logoUrl: "",
    maintenanceMode: false,
    supportEmail: "support@pulo.com",
    services: {
      rides: true,
      stays: true,
      puloAssistants: true,
    },
  });

  const [securitySettings, setSecuritySettings] = useState({
    mfaEnabled: true,
    authenticatorApp: true,
    smsVerification: true,
    biometricPasskey: true,
    globalSessionExpiry: "15 Minutes",
    minPasswordLength: 12,
    passwordRequirements: {
      numbers: true,
      specialChars: true,
      caseSensitivity: true,
    },
    autoTimeoutDuration: "30 Minutes",
    concurrentSessions: true,
    recentEvents: [],
  });

  const [logoPreview, setLogoPreview] = useState(null);

  // Sync settings prop to state
  useEffect(() => {
    if (settings) {
      if (settings.platformConfiguration) {
        setPlatformConfig({
          appName: settings.platformConfiguration.appName ?? "Pulo",
          primaryCurrency: settings.platformConfiguration.primaryCurrency ?? "NGN - Naira",
          systemTimezone: settings.platformConfiguration.systemTimezone ?? "(GMT +1) West Africa",
          logoUrl: settings.platformConfiguration.logoUrl ?? "",
          maintenanceMode: settings.platformConfiguration.maintenanceMode ?? false,
          supportEmail: settings.platformConfiguration.supportEmail ?? "support@pulo.com",
          services: {
            rides: settings.platformConfiguration.services?.rides ?? true,
            stays: settings.platformConfiguration.services?.stays ?? true,
            puloAssistants: settings.platformConfiguration.services?.puloAssistants ?? true,
          },
        });
        if (settings.platformConfiguration.logoUrl) {
          setLogoPreview(settings.platformConfiguration.logoUrl);
        } else {
          setLogoPreview(null);
        }
      }
      if (settings.securitySettings) {
        setSecuritySettings({
          mfaEnabled: settings.securitySettings.mfaEnabled ?? true,
          authenticatorApp: settings.securitySettings.authenticatorApp ?? true,
          smsVerification: settings.securitySettings.smsVerification ?? true,
          biometricPasskey: settings.securitySettings.biometricPasskey ?? true,
          globalSessionExpiry: settings.securitySettings.globalSessionExpiry ?? "15 Minutes",
          minPasswordLength: settings.securitySettings.minPasswordLength ?? 12,
          passwordRequirements: {
            numbers: settings.securitySettings.passwordRequirements?.numbers ?? true,
            specialChars: settings.securitySettings.passwordRequirements?.specialChars ?? true,
            caseSensitivity: settings.securitySettings.passwordRequirements?.caseSensitivity ?? true,
          },
          autoTimeoutDuration: settings.securitySettings.autoTimeoutDuration ?? "30 Minutes",
          concurrentSessions: settings.securitySettings.concurrentSessions ?? true,
          recentEvents: settings.securitySettings.recentEvents || [],
        });
      }
    }
  }, [settings]);

  const toggleSection = (sectionName) => {
    setExpandedSection((prev) => (prev === sectionName ? null : sectionName));
  };

  const handleGeneralSave = async () => {
    try {
      await updateSettingsMutation.mutateAsync({
        platformConfiguration: platformConfig,
        securitySettings,
      });
      toast.success("System configurations updated successfully.");
    } catch (error) {
      toast.apiError(error, "Failed to update configurations.");
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size exceeds 2MB limit.");
      return;
    }

    // Show an instant local preview while the real upload completes.
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);

    setIsUploadingLogo(true);
    try {
      const response = await adminService.uploadSettingsLogo(file);
      const updatedLogoUrl = response?.data?.platformConfiguration?.logoUrl;
      if (updatedLogoUrl) {
        setPlatformConfig((prev) => ({ ...prev, logoUrl: updatedLogoUrl }));
        setLogoPreview(updatedLogoUrl);
      }
      queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
      queryClient.invalidateQueries({ queryKey: ["public", "appConfig"] });
      toast.success("Logo updated successfully.");
    } catch (error) {
      toast.apiError(error, "Failed to upload logo.");
    } finally {
      setIsUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setPlatformConfig((prev) => ({ ...prev, logoUrl: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHrs < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return diffMins <= 0 ? "Just now" : `${diffMins} minutes ago`;
    }
    if (diffHrs < 24) {
      return `${diffHrs} hour${diffHrs > 1 ? "s" : ""} ago`;
    }
    const diffDays = Math.floor(diffHrs / 24);
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  return (
    <div className="space-y-4 pb-10">
      {/* Platform Configuration Accordion */}
      <div className="rounded-2xl border border-[#EEF0F3] bg-white overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => toggleSection("platform")}
          className="flex w-full items-center justify-between p-5 text-left transition hover:bg-gray-50/50"
        >
          <h2 className="text-xl font-bold text-gray-900">Platform configuration</h2>
          <ChevronDown
            className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
              expandedSection === "platform" ? "rotate-180" : ""
            }`}
          />
        </button>

        {expandedSection === "platform" && (
          <div className="border-t border-[#EEF0F3] p-5 sm:p-6">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {/* Left Column */}
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 border-b pb-2">
                  General Information
                </h3>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">APP NAME</label>
                  <input
                    type="text"
                    value={platformConfig.appName}
                    onChange={(e) =>
                      setPlatformConfig((prev) => ({ ...prev, appName: e.target.value }))
                    }
                    className="w-full rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-base font-medium text-[#344054] focus:outline-none focus:ring-1 focus:ring-[#61013B]"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">PRIMARY CURRENCY</label>
                  <input
                    type="text"
                    value={platformConfig.primaryCurrency}
                    onChange={(e) =>
                      setPlatformConfig((prev) => ({ ...prev, primaryCurrency: e.target.value }))
                    }
                    className="w-full rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-base font-medium text-[#344054] focus:outline-none focus:ring-1 focus:ring-[#61013B]"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">SYSTEM TIMEZONE</label>
                  <input
                    type="text"
                    value={platformConfig.systemTimezone}
                    onChange={(e) =>
                      setPlatformConfig((prev) => ({ ...prev, systemTimezone: e.target.value }))
                    }
                    className="w-full rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-base font-medium text-[#344054] focus:outline-none focus:ring-1 focus:ring-[#61013B]"
                  />
                </div>

                {/* Logo Section */}
                <div className="space-y-4 pt-4">
                  <h4 className="text-sm font-bold text-gray-900">Platform Logo</h4>
                  <div className="flex flex-col sm:flex-row items-center gap-5">
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="flex h-28 w-28 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 transition hover:border-[#61013B] overflow-hidden relative"
                    >
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="h-full w-full object-contain p-2"
                        />
                      ) : (
                        <>
                          <UploadCloud className="h-6 w-6 text-gray-400 mb-1" />
                          <span className="text-xs font-bold text-gray-500">UPLOAD</span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <div className="flex-1 space-y-3 text-center sm:text-left">
                      <div>
                        <p className="text-sm font-bold text-gray-900">Current Logo</p>
                        <p className="text-xs text-gray-500 font-medium leading-relaxed mt-1">
                          Recommended size: 512 x 512px. PNG or SVG format, max 2MB
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-3 items-center justify-center sm:justify-start">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploadingLogo}
                          className="rounded-lg bg-[#61013B] px-4 py-2 text-xs font-bold text-white transition hover:bg-[#4d0130] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isUploadingLogo ? "Uploading..." : "Update Image"}
                        </button>
                        {logoPreview && (
                          <button
                            type="button"
                            onClick={removeLogo}
                            disabled={isUploadingLogo}
                            className="text-xs font-bold text-[#61013B] hover:text-[#4d0130] transition disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 border-b pb-2">
                  System Status
                </h3>
                <Switch
                  checked={platformConfig.maintenanceMode}
                  onChange={(val) =>
                    setPlatformConfig((prev) => ({ ...prev, maintenanceMode: val }))
                  }
                  label="MAINTENANCE MODE"
                  description="When active, the public-facing application will be disabled and display a maintenance message to all users. Administrative access remains unaffected."
                />
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Support Email</label>
                  <input
                    type="email"
                    value={platformConfig.supportEmail}
                    onChange={(e) =>
                      setPlatformConfig((prev) => ({ ...prev, supportEmail: e.target.value }))
                    }
                    className="w-full rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-base font-medium text-[#344054] focus:outline-none focus:ring-1 focus:ring-[#61013B]"
                  />
                  <p className="text-xs text-gray-500 font-medium leading-tight">
                    Primary contact for user inquiry and system alert
                  </p>
                </div>

                <div className="space-y-4 pt-4">
                  <h3 className="text-lg font-bold text-gray-900 border-b pb-2">
                    Service Modules
                  </h3>
                  <div className="space-y-3">
                    <Switch
                      checked={platformConfig.services.rides}
                      onChange={(val) =>
                        setPlatformConfig((prev) => ({
                          ...prev,
                          services: { ...prev.services, rides: val },
                        }))
                      }
                      label="RIDES"
                      description="City transport"
                    />
                    <Switch
                      checked={platformConfig.services.stays}
                      onChange={(val) =>
                        setPlatformConfig((prev) => ({
                          ...prev,
                          services: { ...prev.services, stays: val },
                        }))
                      }
                      label="STAYS"
                      description="Hotel & Apartment bookings"
                    />
                    <Switch
                      checked={platformConfig.services.puloAssistants}
                      onChange={(val) =>
                        setPlatformConfig((prev) => ({
                          ...prev,
                          services: { ...prev.services, puloAssistants: val },
                        }))
                      }
                      label="PULO ASSISTANTS"
                      description="Curated local experience"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Security Settings Accordion */}
      <div className="rounded-2xl border border-[#EEF0F3] bg-white overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => toggleSection("security")}
          className="flex w-full items-center justify-between p-5 text-left transition hover:bg-gray-50/50"
        >
          <h2 className="text-xl font-bold text-gray-900">Security Settings</h2>
          <ChevronDown
            className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
              expandedSection === "security" ? "rotate-180" : ""
            }`}
          />
        </button>

        {expandedSection === "security" && (
          <div className="border-t border-[#EEF0F3] p-5 sm:p-6 space-y-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {/* Left Column */}
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 border-b pb-2">
                  Authentication Protocols
                </h3>
                <div className="space-y-3">
                  <Switch
                    checked={securitySettings.mfaEnabled}
                    onChange={(val) =>
                      setSecuritySettings((prev) => ({ ...prev, mfaEnabled: val }))
                    }
                    label="Multi-Factor Auth (2FA)"
                    description="Required additional verification for all admins"
                  />
                  <Switch
                    checked={securitySettings.authenticatorApp}
                    onChange={(val) =>
                      setSecuritySettings((prev) => ({ ...prev, authenticatorApp: val }))
                    }
                    label="Authenticator App"
                    description="Google, Authy or Microsoft"
                  />
                  <Switch
                    checked={securitySettings.smsVerification}
                    onChange={(val) =>
                      setSecuritySettings((prev) => ({ ...prev, smsVerification: val }))
                    }
                    label="SMS Verification"
                    description="Backup codes via mobile"
                  />
                  <Switch
                    checked={securitySettings.biometricPasskey}
                    onChange={(val) =>
                      setSecuritySettings((prev) => ({ ...prev, biometricPasskey: val }))
                    }
                    label="Biometric Passkey"
                    description="FaceID or Fingerprint"
                  />
                </div>
                <div className="space-y-2 pt-2">
                  <label className="text-sm font-semibold text-gray-700">GLOBAL SESSION EXPIRY</label>
                  <select
                    value={securitySettings.globalSessionExpiry}
                    onChange={(e) =>
                      setSecuritySettings((prev) => ({ ...prev, globalSessionExpiry: e.target.value }))
                    }
                    className="w-full rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-base font-medium text-[#344054] focus:outline-none focus:ring-1 focus:ring-[#61013B]"
                  >
                    <option value="15 Minutes">15 Minutes</option>
                    <option value="30 Minutes">30 Minutes</option>
                    <option value="1 Hour">1 Hour</option>
                    <option value="24 Hours">24 Hours</option>
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 border-b pb-2">
                  Password Policy
                </h3>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Minimum Password Length</label>
                  <input
                    type="number"
                    value={securitySettings.minPasswordLength}
                    onChange={(e) =>
                      setSecuritySettings((prev) => ({
                        ...prev,
                        minPasswordLength: Number(e.target.value),
                      }))
                    }
                    className="w-full rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-base font-medium text-[#344054] focus:outline-none focus:ring-1 focus:ring-[#61013B]"
                    min="1"
                  />
                  <p className="text-xs text-gray-500 font-medium leading-tight">
                    Recommended length is 12+ characters.
                  </p>
                </div>
                <div className="space-y-3 pt-2">
                  <p className="text-sm font-semibold text-gray-700">Required Special Characters</p>
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="numbers"
                        checked={securitySettings.passwordRequirements.numbers}
                        onChange={(e) =>
                          setSecuritySettings((prev) => ({
                            ...prev,
                            passwordRequirements: {
                              ...prev.passwordRequirements,
                              numbers: e.target.checked,
                            },
                          }))
                        }
                        className="h-5 w-5 rounded border-[#D0D5DD] text-[#61013B] focus:ring-[#61013B]"
                      />
                      <label htmlFor="numbers" className="text-sm font-semibold text-gray-700">
                        Numbers (0-9)
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="special"
                        checked={securitySettings.passwordRequirements.specialChars}
                        onChange={(e) =>
                          setSecuritySettings((prev) => ({
                            ...prev,
                            passwordRequirements: {
                              ...prev.passwordRequirements,
                              specialChars: e.target.checked,
                            },
                          }))
                        }
                        className="h-5 w-5 rounded border-[#D0D5DD] text-[#61013B] focus:ring-[#61013B]"
                      />
                      <label htmlFor="special" className="text-sm font-semibold text-gray-700">
                        Special Characters (!@#$%)
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="case"
                        checked={securitySettings.passwordRequirements.caseSensitivity}
                        onChange={(e) =>
                          setSecuritySettings((prev) => ({
                            ...prev,
                            passwordRequirements: {
                              ...prev.passwordRequirements,
                              caseSensitivity: e.target.checked,
                            },
                          }))
                        }
                        className="h-5 w-5 rounded border-[#D0D5DD] text-[#61013B] focus:ring-[#61013B]"
                      />
                      <label htmlFor="case" className="text-sm font-semibold text-gray-700">
                        Uppercase & Lowercase
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-[#EEF0F3]">
                  <h3 className="text-lg font-bold text-gray-900">Session Management</h3>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Auto-timeout Duration</label>
                    <select
                      value={securitySettings.autoTimeoutDuration}
                      onChange={(e) =>
                        setSecuritySettings((prev) => ({ ...prev, autoTimeoutDuration: e.target.value }))
                      }
                      className="w-full rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-base font-medium text-[#344054] focus:outline-none focus:ring-1 focus:ring-[#61013B]"
                    >
                      <option value="15 Minutes">15 Minutes</option>
                      <option value="30 Minutes">30 Minutes</option>
                      <option value="1 Hour">1 Hour</option>
                    </select>
                  </div>
                  <Switch
                    checked={securitySettings.concurrentSessions}
                    onChange={(val) =>
                      setSecuritySettings((prev) => ({ ...prev, concurrentSessions: val }))
                    }
                    label="Concurrent Sessions"
                    description="Allow multiple simultaneous logins from different device."
                  />
                </div>
              </div>
            </div>

            {/* Bottom Section: Recent Events */}
            <div className="border-t border-[#EEF0F3] pt-6 space-y-4">
              <h3 className="text-lg font-bold text-gray-900">Recent Security Events</h3>
              <div className="divide-y divide-[#EEF0F3]">
                {securitySettings.recentEvents.map((event, index) => (
                  <div key={index} className="flex justify-between items-center py-4 first:pt-2 last:pb-2">
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-gray-900">{event.eventType}</p>
                      <p className="text-xs text-gray-500 font-medium leading-relaxed">
                        {event.details}
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-gray-500">
                      {formatTimeAgo(event.timestamp)}
                    </span>
                  </div>
                ))}
                {securitySettings.recentEvents.length === 0 && (
                  <p className="text-gray-500 italic text-sm">No recent events logged.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action button */}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={handleGeneralSave}
          disabled={updateSettingsMutation.isPending}
          className="rounded-xl bg-[#61013B] px-6 py-3 text-base font-semibold text-white transition hover:bg-[#4d0130] disabled:cursor-not-allowed disabled:opacity-60 shadow-sm"
        >
          {updateSettingsMutation.isPending ? "Saving..." : "Save changes"}
        </button>
      </div>
    </div>
  );
}
